import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicReviewForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/customer-reviews/public/${token}`)
      .then((r) => r.ok ? r.json() : r.json().then((j) => Promise.reject(j)))
      .then((d) => {
        setData(d);
        setName(d.customer_name || "");
        setEmail(d.customer_email || "");
      })
      .catch((e) => setError(e?.detail || "Link invalid sau expirat."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (rating < 1) { setError("Te rugăm alege un rating (1-5 stele)."); return; }
    if ((description || "").trim().length < 10) { setError("Descrierea trebuie să aibă cel puțin 10 caractere."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/customer-reviews/public/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, title, description, customer_name: name, customer_email: email }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Trimiterea a eșuat.");
      setDone(j);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-3">Ups!</h1>
          <p className="text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
        <div data-testid="review-success" className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400 mb-5" />
          <h1 className="text-2xl font-bold mb-3 text-emerald-100">Mulțumim pentru recenzie!</h1>
          <p className="text-slate-200 mb-4">Codul tău de reducere a plecat pe email.</p>
          <div className="rounded-xl border-2 border-dashed border-emerald-500/50 bg-slate-900 p-5 my-6">
            <div className="text-xs text-emerald-300/70 uppercase tracking-wider">Codul tău</div>
            <div className="text-3xl font-bold text-emerald-300 mt-2 font-mono">{done.coupon_code}</div>
          </div>
          <p className="text-xs text-slate-400">Îl poți folosi la următoarea comandă pe bragus.ro.</p>
        </div>
      </div>
    );
  }

  if (data.already_submitted) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
        <div data-testid="review-already-submitted" className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-3">Ai lăsat deja o recenzie</h1>
          <p className="text-slate-300">Îți mulțumim! Codul de reducere ți-a fost trimis pe email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      <div className="max-w-lg mx-auto">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 shadow-2xl">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Lasă o recenzie</h1>
          <p className="text-sm text-slate-400 mb-6">Comanda #{data.order_number} · Părerea ta contează pentru noi. Durează un minut și primești un cod de reducere.</p>

          {/* Product card */}
          <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 mb-6 flex items-center gap-4">
            {data.product.image ? (
              <img src={data.product.image} alt={data.product.name} className="h-20 w-20 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-slate-800 flex-shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Produsul tău</div>
              <div className="text-sm font-medium text-slate-100 line-clamp-3">{data.product.name}</div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="review-form">
            {/* Stars */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-2">Cum ți se pare produsul? <span className="text-rose-400">*</span></label>
              <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`h-9 w-9 transition-colors ${
                        n <= (hoverRating || rating)
                          ? "fill-amber-400 text-amber-400"
                          : "text-slate-600"
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-3 text-sm font-semibold text-amber-400">{rating}/5</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Titlu recenzie (opțional)</label>
              <input
                data-testid="review-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                className="w-full rounded-md border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
                placeholder="Ex: Foarte mulțumit(ă) de produs"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Descrie-ne experiența ta <span className="text-rose-400">*</span></label>
              <textarea
                data-testid="review-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={2000}
                className="w-full rounded-md border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
                placeholder="Cum a fost livrarea? Cum ți se pare produsul? Cu ce te-a ajutat?"
              />
              <div className="mt-1 text-[11px] text-slate-500">{description.length}/2000</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Numele tău</label>
                <input
                  data-testid="review-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Email (pentru cupon)</label>
                <input
                  data-testid="review-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
                />
              </div>
            </div>

            {error && (
              <div data-testid="review-error" className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                {error}
              </div>
            )}

            <button
              data-testid="review-submit"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-400 transition disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? "Se trimite..." : "Trimite recenzia"}
            </button>
            <p className="text-[11px] text-center text-slate-500">După trimitere primești un cod de reducere pe email.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
